<template>
  <div class="card__wrapper">
    <div class="card">
      <h1 class="card__title">{{ this.isAdmin ? this.$i18n.t('messages.orders_all') : this.$i18n.t('messages.my_orders') }}</h1>
      <div class="card__description">
        <div>
          <div class="mb-2" :class="{ 'd-flex-responsive-order': isAdmin }">
            <div class="width-50 details-margin align-items-center" v-if="isAdmin">
              <div class="mr-3 d-flex align-items-center mt-2">
                <span class="font-weight-bold">{{ $t('messages.pickup') }}:</span>
                <b-form-select v-model="params.pickupPointId" class="ml-3">
                  <option :value="null"></option>
                  <option v-for="pickupPoint in pickupPoints" :key="'pp-' + pickupPoint.id" :value="pickupPoint.id">
                    {{ pickupPoint.city }}, {{ $t('countries.' + pickupPoint.country) }}
                  </option>
                </b-form-select>
              </div>
              <div class="mr-3 d-flex align-items-center mt-2">
                <span class="font-weight-bold">{{ $t('messages.customer') }}:</span>
                <b-form-select v-model="params.userId" class="ml-3">
                  <option :value="null"></option>
                  <option v-for="user in users" :key="'u-' + user.id" :value="user.id">{{ user.full_name }}</option>
                </b-form-select>
              </div>
              <div class="mr-3 d-flex align-items-center mt-2">
                <span class="font-weight-bold">{{ $t('messages.product') }}:</span>
                <b-form-select v-model="params.productId" class="ml-3">
                  <option :value="null"></option>
                  <option v-for="product in products" :key="'p-' + product.id" :value="product.id">
                    {{ product.name }} (S{{ product.stock ? product.stock : 0 }} / R{{ product.reserved }} / D{{ product.delivered }})
                  </option>
                </b-form-select>
              </div>
              <div class="d-flex-responsive-order mt-2 mb-2">
                <div class="mr-3">
                  <b-form-checkbox v-model="params.delivered" class="checkbox-fix font-weight-bold">{{ $t('messages.delivered') }}</b-form-checkbox>
                </div>
                <div class="mr-3">
                  <b-form-checkbox v-model="params.deliverable" class="checkbox-fix font-weight-bold">{{ $t('messages.deliverable') }}</b-form-checkbox>
                </div>
                <div class="mr-3">
                  <b-form-checkbox v-model="params.paid" class="checkbox-fix font-weight-bold">{{ $t('statuses.paid') }}</b-form-checkbox>
                </div>
                <div class="mr-3">
                  <b-form-checkbox v-model="params.shipping" class="checkbox-fix font-weight-bold">{{ $t('messages.shipping') }}</b-form-checkbox>
                </div>
              </div>
            </div>
            <div class="d-flex width-50 align-items-end justify-content-end mb-3" v-if="orders.length > 0">
              <div class="mr-3 d-flex align-items-center">
                <font-awesome-icon icon="check-circle" class="green mr-2" size="2x"></font-awesome-icon>
                {{ $t('messages.delivered') }}
              </div>
              <div class="mr-3 d-flex align-items-center">
                <font-awesome-icon icon="check-circle" class="yellow mr-2" size="2x"></font-awesome-icon>
                {{ $t('messages.delivered_partly') }}
              </div>
              <div class="mr-1 d-flex align-items-center">
                <font-awesome-icon icon="times-circle" class="red mr-2" size="2x"></font-awesome-icon>
                {{ $t('messages.delivered_not_yet') }}
              </div>
            </div>
          </div>
          <b-table striped bordered stacked="md" :items="orders" :fields="fields"
            @row-clicked="navigateToOrder" v-if="orders.length > 0">
            <template v-slot:cell(status)="row">
              {{ $t('statuses.' + row.item.status) }}
            </template>

            <template v-slot:cell(comments)="row">
              <nl2br tag="p" :text="row.item.comments" v-if="row.item.comments" />
            </template>

            <template v-slot:cell(price)="row">
              € {{ row.item.price | round(2) }}
            </template>

            <template v-slot:cell(created_date)="row">
              {{ formatDate(row.item.created_date) }}
            </template>

            <template v-slot:cell(reserved)="row">
              {{ row.item.reserved }}
            </template>

            <template v-slot:cell(delivered)="row">
              <font-awesome-icon icon="check-circle" class="green" v-if="row.item.delivered === row.item.total" size="2x"></font-awesome-icon>
              <font-awesome-icon icon="check-circle" class="yellow" v-if="row.item.delivered > 0 && row.item.delivered < row.item.total" size="2x"></font-awesome-icon>
              <font-awesome-icon icon="times-circle" class="red" v-if="row.item.delivered === 0" size="2x"></font-awesome-icon>
            </template>

            <template v-slot:cell(view)="row">
              <b-link :to="'/order/' + row.item.id">{{ $t('messages.order_view') }}</b-link>
            </template>
          </b-table>
          <div class="load-more-wrapper">
            <b-button v-if="hasMore" variant="primary" class="w-100" @click="loadMore">
              {{ $t('messages.load_more') }}
            </b-button>
          </div>
        </div>
        <span v-if="orders.length === 0" class="font-italic">{{ $t('messages.orders_empty_all') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defaultMixin } from '../../mixin/default'
import { navigationMixin } from '../../mixin/navigation'

import { productService } from '../../services/product.service'
import { orderService } from '../../services/order.service'

export default {
  name: 'orders',
  metaInfo: function () {
    return {
      title: (this.isAdmin ? this.$i18n.t('messages.orders_all') : this.$i18n.t('messages.my_orders')) + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': (this.isAdmin ? this.$i18n.t('messages.orders_all') : this.$i18n.t('messages.my_orders')),
          'vmid': 'og:title'
        }
      ]
    }
  },
  mixins: [ defaultMixin, navigationMixin ],
  data: function () {
    return {
      fields: [{
        key: 'id',
        label: this.$i18n.t('messages.order_number'),
        tdClass: 'td-info'
      }, {
        key: 'status',
        label: this.$i18n.t('messages.status')
      }, {
        key: 'comments',
        label: this.$i18n.t('messages.comments'),
        tdClass: 'td-info-large'
      }, {
        key: 'price',
        label: this.$i18n.t('messages.price'),
        tdClass: 'td-info-small'
      }, {
        key: 'created_date',
        label: this.$i18n.t('messages.date'),
        tdClass: 'td-info'
      }, {
        key: 'total',
        label: this.$i18n.t('messages.products'),
        tdClass: 'td-info-small'
      }, {
        key: 'reserved',
        label: this.$i18n.t('messages.reserved'),
        tdClass: 'td-info-small'
      }, {
        key: 'delivered',
        label: this.$i18n.t('messages.processed'),
        tdClass: 'td-info-small'
      },
      {
        key: 'view',
        label: '',
        tdClass: 'td-info-small view-order'
      }],
      params: {
        delivered: this.$route.query.delivered ? JSON.parse(this.$route.query.delivered) : false,
        deliverable: this.$route.query.deliverable ? JSON.parse(this.$route.query.deliverable) : false,
        paid: this.$route.query.paid ? JSON.parse(this.$route.query.paid) : false,
        shipping: this.$route.query.shipping ? JSON.parse(this.$route.query.shipping) : false,
        pickupPointId: this.$route.query.pickupPointId ? parseInt(this.$route.query.pickupPointId) : null,
        userId: this.$route.query.userId ? parseInt(this.$route.query.userId) : null,
        productId: this.$route.query.productId ? parseInt(this.$route.query.productId) : null,
        page: this.$route.query.page ? parseInt(this.$route.query.page) : 1
      },
      pickupPoints: [],
      users: [],
      products: [],
      size: 5,
      hasMore: false,
      initialized: false,
      orders: []
    }
  },
  methods: {
    formatDate: function (date) {
      let translatedDate = this.translateDate(date).split(' ')
      return translatedDate[0] + ' ' + translatedDate[1] + ' ' + translatedDate[2]
    },
    fetchOrders: function () {
      let loader = this.$loading.show()
      if (this.isAdmin) {
        orderService.fetchAll(this.initialized ? this.params.page : 1, this.initialized ? this.size : (this.params.page * this.size),
          this.params.pickupPointId, this.params.userId, this.params.productId, this.params.delivered, this.params.deliverable, this.params.shipping, this.params.paid)
          .then((orders) => {
            this.processOrders(loader, orders)
          }, () => {
            this.$store.commit('error', 'errors.orders')
            loader.hide()
            this.initialized = true
          })
      } else {
        orderService.fetch(this.initialized ? this.params.page : 1, this.initialized ? this.size : (this.params.page * this.size))
          .then((orders) => {
            this.processOrders(loader, orders)
          }, () => {
            this.$store.commit('error', 'errors.orders')
            loader.hide()
            this.initialized = true
          })
      }
    },
    processOrders: function (loader, orders) {
      if (this.params.page === 1) {
        this.orders = orders
      } else {
        this.orders = this.orders.concat(orders)
      }
      this.hasMore = orders.length === (this.initialized ? this.size : (this.params.page * this.size))
      loader.hide()
      if (!this.initialized) {
        this.$store.commit('moveToScrollPosition')
      }
      this.initialized = true
    },
    fetchPickupPoints: function () {
      productService.fetchAllPickupPoints()
        .then((pickupPoints) => {
          this.pickupPoints = pickupPoints
        }, () => {
          this.$store.commit('error', 'errors.order_user')
        })
    },
    fetchUsers: function () {
      orderService.fetchUsers()
        .then((users) => {
          this.users = users
        }, () => {
          this.$store.commit('error', 'errors.order_user')
        })
    },
    fetchProducts: function () {
      orderService.fetchProducts()
        .then((products) => {
          this.products = products
        }, () => {
          this.$store.commit('error', 'errors.order_product')
        })
    },
    navigateToOrder: function (row) {
      this.$router.push('/order/' + row.id)
    },
    loadMore: function () {
      this.params.page += 1
      this.load()
    },
    resetAndLoad: function () {
      this.params.page = 1
      this.load()
    },
    load: function () {
      let params = this.params
      let query = 'page='.concat(this.params.page)
      if (this.isAdmin) {
        query = this.generateQueryParams(params)
      }
      this.pushUrlState(query)
      this.fetchOrders()
    }
  },
  watch: {
    'params.pickupPointId': function () {
      this.resetAndLoad()
    },
    'params.userId': function () {
      this.resetAndLoad()
    },
    'params.productId': function () {
      this.resetAndLoad()
    },
    'params.delivered': function () {
      this.resetAndLoad()
    },
    'params.deliverable': function () {
      this.resetAndLoad()
    },
    'params.paid': function () {
      this.resetAndLoad()
    },
    'params.shipping': function () {
      this.resetAndLoad()
    }
  },
  created: function () {
    this.fetchOrders()
    if (this.isAdmin) {
      this.fetchPickupPoints()
      this.fetchUsers()
      this.fetchProducts()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .d-flex-responsive-order {
    display: block;
  }

  .table >>> .view-order {
    text-align: center !important;
  }

  @media screen and (min-width: 768px) {
    .table >>> .td-info {
      width: 150px !important;
    }

    .table >>> .td-info-small {
      width: 100px !important;
    }

    .table >>> .td-info-large {
      width: 300px !important;
    }

    .d-flex-responsive-order {
      display: -webkit-flex; /* Safari */
      display: flex;
    }

    .d-flex-responsive-order .width-50 {
      width: 50%;
    }

    .table >>> .view-order {
      text-align: inherit !important;
    }
  }

  .table >>> td {
    vertical-align: middle !important;
    cursor: pointer;
  }

  .checkbox-fix >>> label {
    padding-top: 2px !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/product/Orders.vue